/**
 * El cable de coordinación. Un solo lugar que sepa qué rutas existen.
 *
 * Todo viaja con `credentials: "include"`: sin la cookie de sesión la API
 * responde 401, y sin `Origin` —que el navegador pone solo— el middleware de
 * CSRF responde 403. Los dos errores se parecen a un problema de permisos y
 * no lo son; está escrito aquí para no volver a diagnosticarlo.
 */

export interface Pantalla {
  id: string;
  titulo: string;
  orden: number;
  /** Por trabajo, no por módulo (db/045): lo decide el servidor. */
  grupo: "mi_zona" | "captacion" | "administracion" | "otro";
}

export interface NodoTerritorio {
  id: number;
  tipo: "estado" | "municipio" | "distrito" | "zona" | "seccion" | "manzana";
  clave: string;
  nombre: string;
  padre_id: number | null;
}

export interface Yo {
  usuario: string;
  nombre: string;
  pantallas: Pantalla[];
  capacidades: Record<string, boolean>;
  territorio: Omit<NodoTerritorio, "padre_id"> | null;
  alcance: { secciones: number[]; municipios: number[]; todo: boolean };
}

export interface Capacidad {
  id: string;
  descripcion: string;
}

export interface Usuario {
  id: number;
  usuario: string;
  nombre: string;
  activo: boolean;
  territorio_id: number | null;
  capacidades: Record<string, boolean>;
  creado: string;
  /** Generada en la base (db/045): el hash jamás viaja, el hecho sí. */
  tiene_contrasena?: boolean;
}

export class ErrorDeCable extends Error {
  constructor(
    readonly estado: number,
    readonly detalle: unknown,
  ) {
    super(typeof detalle === "string" ? detalle : "la petición no se pudo completar");
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const respuesta = await fetch(ruta, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opciones.headers ?? {}) },
    ...opciones,
  });
  if (!respuesta.ok) {
    let detalle: unknown = respuesta.statusText;
    try {
      const cuerpo = (await respuesta.json()) as { detail?: unknown };
      detalle = cuerpo.detail ?? detalle;
    } catch {
      // Un error sin JSON sigue siendo un error: no se traga.
    }
    throw new ErrorDeCable(respuesta.status, detalle);
  }
  return (await respuesta.json()) as T;
}

// --------------------------------------------------------------------- acceso

export function entrar(usuario: string, password: string) {
  return pedir<{ ok: true }>("/acceso/entrar", {
    method: "POST",
    body: JSON.stringify({ usuario, password }),
  });
}

export function salir() {
  return pedir<{ ok: true }>("/acceso/salir", { method: "POST" });
}

export function quienSoy() {
  return pedir<Yo>("/yo");
}

// ------------------------------------------------------------------- catálogos

export function listarCapacidades() {
  return pedir<{ capacidades: Capacidad[] }>("/capacidades");
}

export function listarTerritorio() {
  return pedir<{ nodos: NodoTerritorio[] }>("/territorio");
}

export interface PerfilDelServidor {
  clave: string;
  nombre: string;
  descripcion: string;
  capacidades: Record<string, boolean>;
}

/** Los oficios de la campaña (db/044). Solo quien reparte cuentas. */
export function listarPerfiles() {
  return pedir<{ perfiles: PerfilDelServidor[] }>("/perfiles");
}

// -------------------------------------------------------------------- cuentas
// El recurso lo deriva el generador del manifiesto (D023), así que la forma de
// la respuesta la decide el servidor: `{filas, desde, limite}`. Escribirla a
// mano aquí como una lista pelada costó una prueba en rojo.

export function listarUsuarios(limite = 200, desde = 0) {
  const p = new URLSearchParams({ limite: String(limite), desde: String(desde) });
  return pedir<{ filas: Usuario[]; desde: number; limite: number }>(
    "/usuarios?" + p.toString(),
  );
}

export interface AltaDeUsuario {
  usuario: string;
  nombre: string;
  territorio_id: number | null;
  capacidades: Record<string, boolean>;
  activo?: boolean;
}

export function crearUsuario(datos: AltaDeUsuario) {
  return pedir<Usuario>("/usuarios", { method: "POST", body: JSON.stringify(datos) });
}

/**
 * Los cambios van como PARCHE: solo lo que cambió. El recurso acepta un
 * subconjunto de `editables`, y mandar el objeto entero haría que reactivar a
 * alguien reescribiera de paso sus capacidades con lo que el navegador tuviera
 * en memoria — que puede ser de hace diez minutos.
 */
export function editarUsuario(id: number, cambios: Partial<AltaDeUsuario>) {
  return pedir<Usuario>("/usuarios/" + id, {
    method: "PATCH",
    body: JSON.stringify(cambios),
  });
}

/**
 * Le pone contraseña NUEVA a una cuenta y la devuelve una sola vez.
 *
 * No va por `/usuarios` porque `hash_password` no es un campo editable del
 * recurso y hace bien en no serlo: un campo de contraseña dentro de un CRUD
 * genérico termina recibiendo texto plano. La genera el servidor; aquí no se
 * manda ninguna.
 */
export function darContrasena(id: number) {
  return pedir<{
    id: number;
    usuario: string;
    contrasena: string;
    sesiones_revocadas: number;
  }>("/usuarios/" + id + "/contrasena", { method: "POST" });
}

// ----------------------------------------------------------------------- mapa
// Geometría: la contesta la plataforma, y por eso estas dos rutas solo
// existen cuando la campaña tiene cartografía configurada. El cliente trata
// su ausencia (404) como «abre en la entidad», no como un error.

export interface Recuadro {
  oeste: number;
  sur: number;
  este: number;
  norte: number;
}

export interface Vista {
  recuadro: Recuadro;
  secciones: number;
  motivo: "ambito" | "sin_ambito" | "sin_cartografia";
  todo: boolean;
}

export interface SeccionUbicada {
  seccion: number;
  municipio: number;
  distrito_local: number | null;
  punto: { lon: number; lat: number };
  recuadro: Recuadro;
  en_mi_ambito: boolean;
}

export function verVista() {
  return pedir<Vista>("/mapa/vista");
}

export function ubicarSeccion(numero: number) {
  return pedir<SeccionUbicada>("/mapa/seccion/" + numero);
}

export interface NodoUbicado {
  id: number;
  tipo: NodoTerritorio["tipo"];
  clave: string;
  nombre: string;
  secciones: number;
  /** `null` cuando el nodo no tiene secciones con geometría en el corte. */
  recuadro: Recuadro | null;
}

/** Dónde está un territorio entero (municipio, distrito, zona), para ir a él. */
export function ubicarNodo(id: number) {
  return pedir<NodoUbicado>("/mapa/nodo/" + id);
}

// -------------------------------------------------------------------- tablero
// El centro de mando (db/045 + api/app/rutas/tablero.py). Todo acotado por la
// RLS de quien pregunta; `visible` dice qué bloques NO puede ver la sesión,
// para esconderlos en vez de pintarlos en cero.

export interface Proyeccion {
  estado: "cumplida" | "sin_ritmo" | "vencida" | "a_tiempo" | "en_riesgo" | "en_marcha";
  faltante: number;
  ritmo_diario: number;
  dias_restantes: number | null;
  fecha_estimada: string | null;
  ritmo_necesario: number | null;
  porcentaje: number;
}

export interface Meta {
  id: number;
  territorio_id: number;
  tipo: NodoTerritorio["tipo"];
  clave: string;
  nombre: string;
  medida: string;
  objetivo: number;
  periodo_inicio: string | null;
  periodo_fin: string | null;
  logrado: number;
  faltante: number;
  ultimos_14_dias: number;
  proyeccion: Proyeccion;
}

export interface Alerta {
  nivel: "alta" | "media" | "baja";
  clave: string;
  titulo: string;
  detalle: string;
  accion: { pantalla: string; parametros: Record<string, unknown>; texto: string };
}

export interface Evento {
  id: number;
  ocurrido: string;
  accion: string;
  entidad: string | null;
  entidad_id: string | null;
  quien: string | null;
  detalle: Record<string, unknown>;
}

export interface Tablero {
  hoy: string;
  calculado: string;
  campana: Campana | null;
  dias_a_jornada: number | null;
  marcador: Marcador;
  proyeccion: IndicadorProyeccion[];
  visible: {
    bandeja: boolean;
    cuentas: boolean;
    operacion: boolean;
    captacion: boolean;
    confianza: boolean;
    eventos: boolean;
    metas: boolean;
    estructura: boolean;
  };
  cifras: {
    padron: {
      personas: number;
      hogares: number;
      sin_seccion: number;
      ultimos_7_dias: number;
      ultimos_14_dias: number;
      por_captura: number;
      por_captacion: number;
      por_importacion: number;
      con_telefono: number;
    };
    territorio: {
      secciones: number;
      con_captura: number;
      con_pendientes: number;
      /** Lo que cae en TUS secciones; `padron.personas` puede ser más (db/009). */
      personas_en_ambito: number;
    };
    bandeja?: {
      pendientes_sin_tomar: number;
      en_revision: number;
      pendientes: number;
      rechazadas: number;
      aprobadas_7_dias: number;
      mas_vieja_dias: number;
    };
    cuentas?: { activas: number; inactivas: number; sin_contrasena: number; capturistas: number };
    operacion?: { abiertas: number; intentos_hoy: number; intentos_7_dias: number };
    captacion?: { enlaces_vivos: number; registros_7_dias: number; referentes: number };
    confianza?: {
      capturistas: { capturista_id: number; nombre: string; puntos: number; resumen: string }[];
    };
    estructura?: { roles: RolEstructura[]; con_plan: boolean; plan_es_de_toda_la_campana: boolean };
  };
  metas: Meta[];
  alertas: Alerta[];
  actividad: Evento[];
}

export function verTablero() {
  return pedir<Tablero>("/tablero");
}

export interface NodoConCifras {
  id: number;
  tipo: NodoTerritorio["tipo"];
  clave: string;
  nombre: string;
  padre_id: number | null;
  secciones: number;
  en_ambito: number;
  con_captura: number;
  ciclo: boolean;
  personas: number;
  hogares: number;
  nivel_casa: number;
  captadas: number;
  pendientes: number;
  rechazadas: number;
  ultimos_7_dias: number;
  ultimos_14_dias: number;
  verificados: number;
}

export function verTerritorioConCifras() {
  return pedir<{ nodos: NodoConCifras[] }>("/tablero/territorio");
}

export interface SeccionConCifras {
  seccion: number;
  nodo_id: number;
  padre_id: number | null;
  personas: number;
  hogares: number;
  nivel_casa: number;
  captadas: number;
  pendientes: number;
  rechazadas: number;
  ultimos_7_dias: number;
  ultimos_14_dias: number;
  ultima_captura: string | null;
  verificados: number;
  /** db/047: la estructura vigente en la sección (cero sin ver_estructura). */
  responsables: number;
  seccionales: number;
  manzaneras: number;
  rc: number;
  rg: number;
  en_ambito: boolean;
  /** De la plataforma + supuestos (db/046); null fuera del ámbito o sin ver_metas. */
  lista_nominal: number | null;
  meta_votos: number | null;
  meta_origen: string | null;
  cobertura_pct: number | null;
  avance_pct: number | null;
}

export function verSecciones() {
  return pedir<{ secciones: SeccionConCifras[]; metas_visibles: boolean }>("/tablero/secciones");
}

// ---------------------------------------------------------------------- metas
// El recurso lo deriva el manifiesto (db/005): mismas formas que /usuarios.

export interface MetaNueva {
  territorio_id: number;
  objetivo: number;
  periodo_inicio: string | null;
  periodo_fin: string | null;
}

export function crearMeta(datos: MetaNueva) {
  return pedir<{ id: number }>("/metas", {
    method: "POST",
    body: JSON.stringify({ ...datos, tipo: "personas" }),
  });
}

export function editarMeta(id: number, cambios: Partial<MetaNueva>) {
  return pedir<{ id: number }>("/metas/" + id, {
    method: "PATCH",
    body: JSON.stringify(cambios),
  });
}

// ------------------------------------------------------------------- bitácora
// También del manifiesto (db/002). Viene ordenada por id, que es el orden de
// llegada; la pantalla la invierte para leer lo último primero.

export interface EventoCrudo {
  id: number;
  ocurrido: string;
  usuario_id: number | null;
  accion: string;
  entidad: string | null;
  entidad_id: string | null;
  detalle: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
}

export function listarEventos(limite = 200, desde = 0) {
  const p = new URLSearchParams({ limite: String(limite), desde: String(desde) });
  return pedir<{ filas: EventoCrudo[]; desde: number; limite: number }>(
    "/eventos?" + p.toString(),
  );
}

// ------------------------------------------------------------------ campaña
// db/046: la campaña que ES esta instancia, sus supuestos (versionados) y sus
// anclas. La meta «votos para ganar» se calcula al leer (api/app/votos.py).

export type Contienda =
  | "ayuntamiento"
  | "diputacion_local"
  | "diputacion_federal"
  | "gubernatura"
  | "senaduria"
  | "presidencia";

export interface Campana {
  id: 1;
  nombre: string;
  contienda: Contienda;
  partido: string | null;
  jornada: string | null;
  territorio_id: number | null;
  meta_votos_estructura: number | null;
  actualizada: string;
  territorio_tipo: NodoTerritorio["tipo"] | null;
  territorio_clave: string | null;
  territorio_nombre: string | null;
}

export interface Supuesto {
  id: number;
  clave: "participacion" | "margen" | "techo" | "piso";
  valor: number | string;
  desde: string;
  hasta: string | null;
  motivo: string;
  quien?: string | null;
}

export interface CampanaVista {
  campana: Campana | null;
  dias_a_jornada: number | null;
  secciones: number;
  supuestos: Supuesto[];
  supuestos_sin_ratificar: string[];
  participacion_medida: number | null;
  con_plataforma: boolean;
}

export function verCampana() {
  return pedir<CampanaVista>("/campana");
}

export interface CampanaNueva {
  nombre: string;
  contienda: Contienda;
  partido: string | null;
  jornada: string | null;
  territorio_id: number | null;
  meta_votos_estructura: number | null;
}

export function declararCampana(datos: CampanaNueva) {
  return pedir<{ campana: Campana; dias_a_jornada: number | null }>("/campana", {
    method: "PUT",
    body: JSON.stringify(datos),
  });
}

export function listarSupuestos() {
  return pedir<{ vigentes: Supuesto[]; historial: Supuesto[] }>("/supuestos");
}

export function cambiarSupuesto(clave: Supuesto["clave"], valor: number, motivo: string) {
  return pedir<Supuesto>("/supuestos", {
    method: "POST",
    body: JSON.stringify({ clave, valor, motivo }),
  });
}

export interface Ancla {
  id: number;
  seccion: number;
  votos: number;
  desde: string;
  hasta: string | null;
  motivo: string;
  quien?: string | null;
}

export function listarAnclas() {
  return pedir<{ anclas: Ancla[] }>("/anclas");
}

export function anclar(seccion: number, votos: number, motivo: string) {
  return pedir<Ancla>("/anclas", { method: "POST", body: JSON.stringify({ seccion, votos, motivo }) });
}

export function cerrarAncla(id: number, motivo: string) {
  return pedir<{ id: number; cerrada: true }>("/anclas/" + id + "/cerrar", {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export interface MetaVotosSeccion {
  seccion: number;
  meta: number | null;
  origen: "calculada" | "anclada" | "sin_base" | "sin_parametros";
  lista_nominal: number | null;
  emitidos_esperados: number | null;
  base_rival: number | null;
  rival: string | null;
  propios_referencia: number | null;
  gano_referencia: boolean | null;
  anio_referencia: number | null;
  topada: boolean;
  bruta?: number;
  piso?: number;
  techo?: number;
  faltan?: string[];
}

export interface ResumenVotos {
  secciones: number;
  con_meta: number;
  votos_para_ganar: number | null;
  sin_base: number;
  sin_parametros: number;
  ancladas: number;
  topadas: number;
  lista_nominal: number;
}

export function verVotosPorSeccion() {
  return pedir<{
    contexto: Record<string, unknown>;
    resumen: ResumenVotos;
    secciones: MetaVotosSeccion[];
  }>("/campana/votos");
}

// -------------------------------------------------------- tablero (v2)

export interface Marcador {
  sin_campana: boolean;
  promovidos: number;
  verificados: number;
  secciones: number;
  secciones_con_captura: number;
  /** null cuando la sesión no ve la estructura (no es un cero). */
  secciones_con_responsable: number | null;
  votos:
    | (ResumenVotos & {
        meta_estructura: number | null;
        meta_estructura_es_de_toda_la_campana: boolean;
        brecha: number | null;
      })
    | null;
  lista_nominal: number | null;
  cobertura_pct: number | null;
  supuestos_sin_ratificar: boolean;
  votos_no_visibles?: boolean;
  contexto?: Record<string, unknown>;
}

export interface IndicadorProyeccion {
  indicador: "promovidos" | "verificados" | string;
  avance: number;
  meta: number | null;
  faltan: number | null;
  ritmo_requerido_dia: number | null;
  ritmo_observado_dia: number | null;
  proyeccion_a_jornada: number | null;
  alcanza: boolean | null;
  sin_base: string | null;
  serie: { semana: string; n: number }[];
}

export interface FilaDesglose {
  id: number;
  tipo: NodoTerritorio["tipo"];
  clave: string;
  nombre: string;
  seccion: number | null;
  en_ambito: boolean;
  tiene_hijos: boolean;
  secciones: number;
  secciones_en_ambito: number;
  con_captura: number;
  con_responsable: number;
  personas: number;
  hogares: number;
  verificados: number;
  pendientes: number;
  ultimos_7_dias: number;
  lista_nominal: number | null;
  cobertura_pct: number | null;
  meta_votos: number | null;
  meta_origen: string | null;
  avance_pct: number | null;
}

export interface Desglose {
  nodo: number | null;
  migas: { id: number; tipo: NodoTerritorio["tipo"]; clave: string; nombre: string }[];
  filas: FilaDesglose[];
  total: {
    personas: number;
    hogares: number;
    verificados: number;
    pendientes: number;
    secciones: number;
    secciones_en_ambito: number;
    con_captura: number;
    con_responsable: number;
    lista_nominal: number | null;
    cobertura_pct: number | null;
    meta_votos: number | null;
    avance_pct: number | null;
  };
  metas_visibles: boolean;
  estructura_visible: boolean;
}

export function verDesglose(nodo: number | null) {
  const p = nodo == null ? "" : "?" + new URLSearchParams({ nodo: String(nodo) }).toString();
  return pedir<Desglose>("/tablero/desglose" + p);
}

// ------------------------------------------------------------- estructura
// db/047: quién responde por qué territorio, con vigencia. Nada se borra.

export interface RolEstructura {
  clave: string;
  nombre: string;
  plural: string;
  ambito_tipo: NodoTerritorio["tipo"][];
  responde_por_seccion: boolean;
  orden: number;
  plan: number | null;
  vigentes: number;
  faltan: number | null;
}

export interface SeccionVacante {
  seccion: number;
  nodo_id: number;
  padre_id: number | null;
  personas: number;
  responsables: number;
  seccionales: number;
  manzaneras: number;
  rc: number;
  rg: number;
}

export interface ResumenEstructura {
  roles: RolEstructura[];
  plan_es_de_toda_la_campana: boolean;
  secciones: number;
  con_responsable: number;
  vacantes: number;
  vacantes_lista: SeccionVacante[];
  puede_administrar: boolean;
}

export function verEstructura() {
  return pedir<ResumenEstructura>("/estructura");
}

export interface Asignacion {
  id: number;
  persona_id: number;
  rol: string;
  rol_nombre: string;
  rol_orden: number;
  territorio_id: number;
  territorio_tipo: NodoTerritorio["tipo"];
  territorio_clave: string;
  territorio_nombre: string;
  seccion: number | null;
  desde: string;
  hasta: string | null;
  nota: string | null;
  motivo_baja: string | null;
  nombre: string;
  primer_apellido: string;
  segundo_apellido: string | null;
  telefono: string | null;
  quien: string | null;
}

export function listarAsignaciones(f: { rol?: string; nodo?: number; q?: string; bajas?: boolean } = {}) {
  const p = new URLSearchParams();
  if (f.rol) p.set("rol", f.rol);
  if (f.nodo != null) p.set("nodo", String(f.nodo));
  if (f.q) p.set("q", f.q);
  if (f.bajas) p.set("bajas", "true");
  const qs = p.toString();
  return pedir<{ asignaciones: Asignacion[] }>("/estructura/asignaciones" + (qs ? "?" + qs : ""));
}

export function nombrar(datos: { persona_id: number; rol: string; territorio_id: number; nota: string | null }) {
  return pedir<{ id: number }>("/estructura/asignaciones", { method: "POST", body: JSON.stringify(datos) });
}

export function darDeBaja(id: number, motivo: string) {
  return pedir<{ id: number; hasta: string }>("/estructura/asignaciones/" + id + "/baja", {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function cambiarAsignacion(id: number, territorio_id: number, motivo: string) {
  return pedir<{ cerrada: { id: number }; nueva: { id: number } }>("/estructura/asignaciones/" + id + "/cambio", {
    method: "POST",
    body: JSON.stringify({ territorio_id, motivo }),
  });
}

export function fijarPlan(posiciones: Record<string, number>) {
  return pedir<{ roles: RolEstructura[] }>("/estructura/plan", { method: "PUT", body: JSON.stringify({ posiciones }) });
}

export interface PersonaDelPadron {
  id: number;
  nombre: string;
  primer_apellido: string;
  segundo_apellido: string | null;
  telefono: string | null;
  seccion: number | null;
  roles: string | null;
}

export function buscarPersonas(f: { q?: string; seccion?: number }) {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.seccion != null) p.set("seccion", String(f.seccion));
  return pedir<{ personas: PersonaDelPadron[] }>("/estructura/personas?" + p.toString());
}

export function listarTerritoriosEstructura() {
  return pedir<{ nodos: NodoTerritorio[] }>("/estructura/territorios");
}
